import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { PlaneTakeoff, PlaneLanding, RefreshCw, Download, Search, AlertTriangle, FileText, LayoutDashboard, X, ChevronDown } from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const isoToday = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const csvEscape = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function TraveldeskDashboardServicios() {
  const [tipo, setTipo] = useState("inicio"); // inicio | fin
  const [desde, setDesde] = useState(isoToday());
  const [hasta, setHasta] = useState(addDays(isoToday(), 7));
  const [cliente, setCliente] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [servicios, setServicios] = useState([]); // multi-select
  const [grupos, setGrupos] = useState([]); // multi-select
  const [grupoInput, setGrupoInput] = useState(""); // texto en el input (autocompletar)
  const [grupoSug, setGrupoSug] = useState(false); // mostrar sugerencias
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const grupoRef = useRef(null);

  const load = useCallback(async (opts = {}) => {
    if (opts.refresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard-servicios/data`, {
        params: {
          tipo, desde, hasta,
          cliente: cliente || "", proveedor: proveedor || "",
          servicios: servicios.join(","), grupos: grupos.join("||"),
          q: q || "",
        },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo cargar el dashboard";
      toast.error(msg);
      if (e?.response?.status === 403) setData({ rows: [], count: 0, filtros: { clientes: [], proveedores: [], servicios: [], grupos: [] } });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tipo, desde, hasta, cliente, proveedor, servicios, grupos, q]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tipo, desde, hasta, cliente, proveedor, servicios, grupos]);

  const filtros = data?.filtros || { clientes: [], proveedores: [], servicios: [], grupos: [] };

  const totalPax = data?.count || 0;
  const uniqueClientes = useMemo(() => {
    const s = new Set(); (data?.rows || []).forEach((r) => r.cliente && s.add(r.cliente)); return s.size;
  }, [data]);
  const uniqueProveedores = useMemo(() => {
    const s = new Set(); (data?.rows || []).forEach((r) => r.proveedor && s.add(r.proveedor)); return s.size;
  }, [data]);

  const descargarCSV = () => {
    if (!data?.rows?.length) return toast.error("No hay filas para exportar");
    const headers = ["Día", "Fecha", "Cliente", "Grupo", "Pasajero", "Servicio", "Proveedor", "Localizador", "Ruta / Destino"];
    const lines = [headers.join(",")];
    data.rows.forEach((r) => {
      lines.push([r.dia, r.fecha, r.cliente, r.grupo, r.pasajero, r.servicio, r.proveedor, r.localizador, r.ruta].map(csvEscape).join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard_servicios_${tipo}_${desde}_a_${hasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const descargarPDF = async () => {
    if (!data?.rows?.length) return toast.error("No hay filas para exportar");
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const M = 36; // margen exterior

      // ═══ Paleta Back Office ═══
      const PD_BLUE = [0, 55, 255];       // #0037FF - primary
      const PD_BLUE_DEEP = [0, 21, 127];  // #00157F - gradient start
      const PD_NIGHT = [10, 18, 48];      // #0A1230 - dark navy
      const PD_CYAN = [4, 204, 218];      // #04CCDA - accent
      const SLATE_900 = [15, 23, 42];
      const SLATE_700 = [51, 65, 85];
      const SLATE_500 = [100, 116, 139];
      const SLATE_400 = [148, 163, 184];
      const SLATE_200 = [226, 232, 240];
      const SLATE_100 = [241, 245, 249];
      const SLATE_50 = [248, 250, 252];
      const CYAN_50 = [236, 254, 255];

      // ── Logo PD blanco (versión white para header azul) ──
      const loadLogo = (url) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth; c.height = img.naturalHeight;
          c.getContext("2d").drawImage(img, 0, 0);
          try { resolve(c.toDataURL("image/png")); } catch { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = `${window.location.origin}${url}`;
      });
      const logoDataUrl = await loadLogo("/pd-logo-white.png");

      // ═══ HEADER — banda azul con gradiente PD (efecto del menú superior) ═══
      const HDR_H = 62;
      // Simular gradient con múltiples franjas
      const steps = 40;
      for (let i = 0; i < steps; i++) {
        const t = i / (steps - 1);
        const r = Math.round(PD_BLUE_DEEP[0] + (PD_BLUE[0] - PD_BLUE_DEEP[0]) * t);
        const g = Math.round(PD_BLUE_DEEP[1] + (PD_BLUE[1] - PD_BLUE_DEEP[1]) * t);
        const b = Math.round(PD_BLUE_DEEP[2] + (PD_BLUE[2] - PD_BLUE_DEEP[2]) * t);
        doc.setFillColor(r, g, b);
        doc.rect((pageW / steps) * i, 0, pageW / steps + 0.5, HDR_H, "F");
      }
      // Línea cyan sutil debajo del header (accent PD)
      doc.setFillColor(...PD_CYAN);
      doc.rect(0, HDR_H, pageW, 2, "F");

      // Logo blanco a la izquierda
      if (logoDataUrl) {
        const imgProps = doc.getImageProperties(logoDataUrl);
        const h = 26;
        const w = (imgProps.width / imgProps.height) * h;
        doc.addImage(logoDataUrl, "PNG", M, (HDR_H - h) / 2, w, h);
      } else {
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.text("pd travel", M, HDR_H / 2 + 5);
      }

      // Título derecha
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Dashboard de Servicios", pageW - M, HDR_H / 2 - 3, { align: "right" });
      // Sub etiqueta cyan
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...PD_CYAN);
      const sub = tipo === "inicio" ? "Pasajeros iniciando servicio" : "Pasajeros terminando servicio";
      doc.text(sub, pageW - M, HDR_H / 2 + 12, { align: "right" });

      // ═══ BARRA META (fecha + generado) ═══
      const metaY = HDR_H + 14;
      const fmtDate = (iso) => {
        try { return new Date(iso + "T00:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
        catch { return iso; }
      };
      const nowStr = new Date().toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...SLATE_500);
      doc.text("PERIODO", M, metaY);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...PD_NIGHT);
      doc.text(`${fmtDate(desde)}  a  ${fmtDate(hasta)}`, M, metaY + 14);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...SLATE_400);
      doc.text(`Generado ${nowStr}`, pageW - M, metaY + 14, { align: "right" });

      // ═══ KPI CARDS (3 tarjetas grandes con acento azul PD) ═══
      const kpisY = metaY + 26;
      const kpiH = 46;
      const gap = 10;
      const totalW = pageW - 2 * M;
      const kW = (totalW - gap * 2) / 3;

      const kpis = [
        { l: "Total pasajeros", v: String(totalPax), accent: PD_BLUE, bg: [239, 246, 255] },
        { l: "Clientes distintos", v: String(uniqueClientes), accent: PD_NIGHT, bg: SLATE_50 },
        { l: "Proveedores distintos", v: String(uniqueProveedores), accent: SLATE_700, bg: SLATE_50 },
      ];
      kpis.forEach((k, i) => {
        const x = M + i * (kW + gap);
        // Card con fondo tenue
        doc.setFillColor(...k.bg);
        doc.roundedRect(x, kpisY, kW, kpiH, 6, 6, "F");
        // Borde suave
        doc.setDrawColor(...SLATE_200);
        doc.setLineWidth(0.5);
        doc.roundedRect(x, kpisY, kW, kpiH, 6, 6, "S");
        // Barra vertical de acento (izquierda)
        doc.setFillColor(...k.accent);
        doc.rect(x, kpisY + 8, 3, kpiH - 16, "F");
        // Label
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(...SLATE_500);
        doc.text(k.l.toUpperCase(), x + 12, kpisY + 15);
        // Valor grande
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(...k.accent);
        doc.text(k.v, x + 12, kpisY + 37);
      });

      // ═══ FILTROS APLICADOS (chips) ═══
      let filtersY = kpisY + kpiH + 16;
      const chips = [];
      if (cliente) chips.push({ k: "Cliente", v: cliente });
      if (proveedor) chips.push({ k: "Proveedor", v: proveedor });
      if (servicios.length === 1) chips.push({ k: "Servicio", v: servicios[0] });
      else if (servicios.length > 1) chips.push({ k: "Servicios", v: `${servicios.length} seleccionados` });
      if (grupos.length === 1) chips.push({ k: "Grupo", v: grupos[0] });
      else if (grupos.length > 1) chips.push({ k: "Grupos", v: `${grupos.length} seleccionados` });
      if (q) chips.push({ k: "Buscar", v: `"${q}"` });
      if (chips.length) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...SLATE_500);
        doc.text("FILTROS APLICADOS", M, filtersY);
        filtersY += 5;
        let x = M;
        chips.forEach((c) => {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          const label = `${c.k}: ${c.v}`;
          const w = doc.getTextWidth(label) + 16;
          doc.setFillColor(...CYAN_50);
          doc.setDrawColor(...PD_CYAN);
          doc.setLineWidth(0.4);
          doc.roundedRect(x, filtersY, w, 15, 7.5, 7.5, "FD");
          doc.setTextColor(...PD_NIGHT);
          doc.text(label, x + 8, filtersY + 10);
          x += w + 6;
        });
        filtersY += 22;
      } else {
        filtersY += 4;
      }

      // ═══ TABLA PRINCIPAL ═══
      const head = [["Día", tipo === "inicio" ? "Fecha inicio" : "Fecha fin", "Cliente", "Grupo", "Pasajero", "Servicio", "Proveedor", "Localizador", "Ruta / Destino"]];
      const body = data.rows.map((r) => [r.dia, r.fecha, r.cliente, r.grupo || "—", r.pasajero, r.servicio, r.proveedor, r.localizador, r.ruta]);

      autoTable(doc, {
        startY: filtersY,
        head, body,
        margin: { left: M, right: M, bottom: 40 },
        theme: "plain",
        styles: {
          font: "helvetica",
          fontSize: 8.4,
          cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
          overflow: "linebreak",
          textColor: SLATE_900,
          valign: "middle",
        },
        headStyles: {
          fillColor: PD_NIGHT,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 7.5,
          halign: "left",
          cellPadding: { top: 8, right: 8, bottom: 8, left: 8 },
        },
        alternateRowStyles: { fillColor: SLATE_50 },
        columnStyles: {
          0: { cellWidth: 48, fontStyle: "bold", textColor: PD_BLUE },
          1: { cellWidth: 56, textColor: SLATE_700, fontStyle: "bold" },
          2: { cellWidth: 90, textColor: SLATE_700 },
          3: { cellWidth: 74, textColor: PD_BLUE, fontStyle: "bold" },
          4: { cellWidth: 108, fontStyle: "bold", textColor: PD_NIGHT },
          5: { cellWidth: 60, textColor: SLATE_700 },
          6: { cellWidth: 80, textColor: SLATE_700 },
          7: { cellWidth: 66, font: "courier", fontSize: 7.6, textColor: PD_NIGHT, fontStyle: "bold" },
          8: { cellWidth: "auto", textColor: SLATE_700 },
        },
        didDrawCell: (info) => {
          // Línea cyan de acento debajo del encabezado (una sola)
          if (info.section === "head" && info.row.index === 0 && info.column.index === 0) {
            const y = info.cell.y + info.cell.height;
            doc.setDrawColor(...PD_CYAN);
            doc.setLineWidth(1.2);
            doc.line(M, y, pageW - M, y);
          }
          // Línea sutil entre filas del body
          if (info.section === "body" && info.column.index === 0) {
            doc.setDrawColor(...SLATE_200);
            doc.setLineWidth(0.25);
            doc.line(M, info.cell.y, pageW - M, info.cell.y);
          }
        },
        didDrawPage: () => {
          // ── FOOTER ──
          doc.setDrawColor(...SLATE_200);
          doc.setLineWidth(0.5);
          doc.line(M, pageH - 30, pageW - M, pageH - 30);

          // Izquierda: marca
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);
          doc.setTextColor(...PD_BLUE);
          doc.text("PD TRAVEL", M, pageH - 15);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...SLATE_400);
          doc.text("  ·  Back Office  ·  Traveldesk", M + doc.getTextWidth("PD TRAVEL"), pageH - 15);

          // Centro: total pax
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(...SLATE_500);
          doc.text(`${totalPax} pasajeros en el periodo`, pageW / 2, pageH - 15, { align: "center" });

          // Derecha: numeración
          const pInfo = doc.internal.getCurrentPageInfo();
          const total = doc.internal.getNumberOfPages();
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);
          doc.setTextColor(...PD_NIGHT);
          doc.text(`Página ${pInfo.pageNumber} de ${total}`, pageW - M, pageH - 15, { align: "right" });
        },
      });

      const fname = `dashboard_servicios_${tipo}_${desde}_a_${hasta}.pdf`;
      doc.save(fname);
      toast.success("PDF descargado");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo generar el PDF");
    }
  };

  const rangoPreset = (dias) => {
    const start = isoToday();
    setDesde(start);
    setHasta(addDays(start, dias - 1));
  };

  return (
    <div className="min-h-[calc(100vh-3rem)] bg-slate-50" data-testid="dashboard-servicios">
      <div className="max-w-[1400px] mx-auto p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <ModuleHeaderIcon icon={LayoutDashboard} />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Dashboard de Servicios
              </h1>
              <p className="text-sm text-slate-500">Pasajeros {tipo === "inicio" ? "iniciando" : "terminando"} servicio en el rango seleccionado.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data?.stale && (
              <Badge className="bg-amber-100 text-amber-800 border-0 gap-1"><AlertTriangle className="w-3 h-3" /> Datos en caché</Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => load({ refresh: true })} disabled={refreshing} data-testid="btn-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refrescar
            </Button>
            <Button variant="outline" size="sm" onClick={descargarCSV} data-testid="btn-download-csv">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
            <Button size="sm" onClick={descargarPDF} className="bg-[#0037ff] hover:bg-[#0028bd] text-white" data-testid="btn-download-pdf">
              <FileText className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tipo</label>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger data-testid="filter-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inicio">Pax iniciando servicio</SelectItem>
                    <SelectItem value="fin">Pax terminando servicio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Desde</label>
                <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="filter-desde" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Hasta</label>
                <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="filter-hasta" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Cliente</label>
                <Select value={cliente || "all"} onValueChange={(v) => setCliente(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-cliente"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">Todos</SelectItem>
                    {filtros.clientes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Proveedor</label>
                <Select value={proveedor || "all"} onValueChange={(v) => setProveedor(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-proveedor"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="all">Todos</SelectItem>
                    {filtros.proveedores.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Servicios (multi-select) */}
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Servicios</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal h-10" data-testid="filter-servicios">
                      <span className="truncate text-left">
                        {servicios.length === 0 ? <span className="text-slate-400">Todos</span>
                          : servicios.length === 1 ? servicios[0]
                          : `${servicios.length} seleccionados`}
                      </span>
                      <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-72" align="start">
                    <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
                      <span className="text-xs font-semibold text-slate-600">{servicios.length} de {filtros.servicios.length}</span>
                      <div className="flex gap-2">
                        <button type="button" className="text-[11px] text-cyan-700 hover:underline" onClick={() => setServicios(filtros.servicios)} data-testid="filter-servicios-todos">Todos</button>
                        <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => setServicios([])} data-testid="filter-servicios-ninguno">Limpiar</button>
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-1.5">
                      {filtros.servicios.length === 0 && <p className="text-xs text-slate-400 px-2 py-3 text-center">Sin servicios disponibles</p>}
                      {filtros.servicios.map((s) => (
                        <label key={s} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                          <Checkbox
                            checked={servicios.includes(s)}
                            onCheckedChange={(v) => setServicios((cur) => v ? [...cur, s] : cur.filter((x) => x !== s))}
                            data-testid={`filter-servicio-chk-${s}`}
                          />
                          <span className="text-sm text-slate-700 truncate">{s}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Nombre del Grupo (multi-select con autocompletar) */}
              <div className="relative">
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Nombre del grupo</label>
                <div className="relative">
                  <Input
                    ref={grupoRef}
                    placeholder={grupos.length === 0 ? "Escribe para buscar…" : `${grupos.length} seleccionado(s)`}
                    value={grupoInput}
                    onChange={(e) => { setGrupoInput(e.target.value); setGrupoSug(true); }}
                    onFocus={() => setGrupoSug(true)}
                    onBlur={() => setTimeout(() => setGrupoSug(false), 180)}
                    data-testid="filter-grupo"
                    className="pr-8"
                  />
                  {(grupoInput || grupos.length > 0) && (
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => { setGrupoInput(""); setGrupos([]); }} data-testid="filter-grupo-clear"><X className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                {grupoSug && (() => {
                  const q = (grupoInput || "").toLowerCase().trim();
                  let base = filtros.grupos || [];
                  if (q) base = base.filter((g) => g.toLowerCase().includes(q));
                  const sug = base.slice(0, 12);
                  if (sug.length === 0 && grupos.length === 0) return null;
                  return (
                    <div className="absolute z-50 top-[70px] left-0 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden min-w-full w-max max-w-[520px]" data-testid="filter-grupo-sugerencias">
                      <div className="flex items-center justify-between px-3 py-1.5 border-b bg-slate-50 text-[11px]">
                        <span className="text-slate-600 font-semibold">{grupos.length} seleccionados</span>
                        {grupos.length > 0 && (
                          <button type="button" className="text-slate-500 hover:text-rose-600" onMouseDown={(e) => { e.preventDefault(); setGrupos([]); }} data-testid="filter-grupo-limpiar">Limpiar</button>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {sug.map((g) => {
                          const sel = grupos.includes(g);
                          return (
                            <button
                              key={g}
                              type="button"
                              className={`w-full text-left px-3 py-1.5 text-sm whitespace-nowrap flex items-center gap-2 ${sel ? "bg-cyan-50 text-cyan-900" : "hover:bg-slate-50 text-slate-700"}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setGrupos((cur) => cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]);
                                // Mantiene el texto escrito para permitir seguir seleccionando otros resultados del mismo filtro
                              }}
                              data-testid={`filter-grupo-sug-${g}`}
                            >
                              <span className={`inline-block w-3.5 h-3.5 rounded border ${sel ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`}>
                                {sel && <svg viewBox="0 0 12 12" className="w-full h-full text-white" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.5 6l2.5 2.5L9.5 3.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                              </span>
                              {g}
                            </button>
                          );
                        })}
                        {sug.length === 0 && q && <p className="text-xs text-slate-400 px-3 py-3">Sin coincidencias para "{q}"</p>}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Buscar</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-8" placeholder="Pasajero, localizador, ruta…" value={q} onChange={(e) => setQ(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && load()} data-testid="filter-q" />
                </div>
              </div>
            </div>

            {/* Chips: servicios y grupos seleccionados */}
            {(servicios.length > 0 || grupos.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="chips-filtros">
                {servicios.length > 0 && (
                  <>
                    <span className="text-xs text-slate-500">Servicios:</span>
                    {servicios.map((s) => (
                      <Badge key={`sv-${s}`} variant="outline" className="gap-1 border-cyan-300 text-cyan-800 bg-cyan-50">
                        {s}
                        <button type="button" onClick={() => setServicios((cur) => cur.filter((x) => x !== s))} className="ml-0.5 hover:text-rose-600" data-testid={`chip-remove-sv-${s}`}><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </>
                )}
                {grupos.length > 0 && (
                  <>
                    <span className="text-xs text-slate-500 ml-1">Grupos:</span>
                    {grupos.map((g) => (
                      <Badge key={`gp-${g}`} variant="outline" className="gap-1 border-indigo-300 text-indigo-800 bg-indigo-50">
                        {g}
                        <button type="button" onClick={() => setGrupos((cur) => cur.filter((x) => x !== g))} className="ml-0.5 hover:text-rose-600" data-testid={`chip-remove-gp-${g}`}><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </>
                )}
                {(servicios.length + grupos.length) > 0 && (
                  <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => { setServicios([]); setGrupos([]); }}>Quitar todos</button>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-xs text-slate-500">Rangos rápidos:</span>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => rangoPreset(1)} data-testid="preset-hoy">Hoy</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => rangoPreset(3)} data-testid="preset-3d">Próx. 3 días</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => rangoPreset(7)} data-testid="preset-7d">Próx. 7 días</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => rangoPreset(30)} data-testid="preset-30d">Próx. 30 días</Button>
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Total pasajeros</p>
            <p className="text-3xl font-bold text-cyan-700 mt-0.5" data-testid="kpi-total">{totalPax}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Clientes distintos</p>
            <p className="text-3xl font-bold text-slate-800 mt-0.5">{uniqueClientes}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Proveedores distintos</p>
            <p className="text-3xl font-bold text-slate-800 mt-0.5">{uniqueProveedores}</p>
          </CardContent></Card>
        </div>

        {/* Tabla */}
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Día</TableHead>
                <TableHead>{tipo === "inicio" ? "Fecha inicio" : "Fecha fin"}</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Grupo</TableHead>
                <TableHead>Pasajero</TableHead>
                <TableHead>Servicio</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Localizador</TableHead>
                <TableHead>Ruta / Destino</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(9)].map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}
                </TableRow>
              ))}
              {!loading && (data?.rows || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-slate-400">
                    Sin resultados para los filtros seleccionados.
                  </TableCell>
                </TableRow>
              )}
              {!loading && (data?.rows || []).map((r, idx) => (
                <TableRow key={idx} className="hover:bg-slate-50" data-testid={`row-${idx}`}>
                  <TableCell className="font-medium text-slate-700">{r.dia}</TableCell>
                  <TableCell className="font-mono text-slate-600">{r.fecha}</TableCell>
                  <TableCell className="text-sm text-slate-700">{r.cliente || "—"}</TableCell>
                  <TableCell className="text-sm text-cyan-800 font-medium">{r.grupo || "—"}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{r.pasajero || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.servicio || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.proveedor || "—"}</TableCell>
                  <TableCell className="text-sm font-mono text-slate-600">{r.localizador || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.ruta || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>

        {data?.stale && (
          <p className="text-xs text-amber-700 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Mostrando última copia disponible desde {data.stale_since}. Reintentando en el próximo refresh.
          </p>
        )}
      </div>
    </div>
  );
}
